/**
 * ClientGalleryViewer Component
 * Premium gallery viewing experience for clients
 * Mobile-first with masonry layout, favorites, and lightbox
 * Supports annotation/markup for proofing workflow
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiHeart, FiDownload, FiMessageSquare, FiX, FiChevronLeft, FiChevronRight, FiArrowLeft, FiLock, FiEdit3, FiGrid, FiMaximize2 } from 'react-icons/fi';
import { FaHeart } from 'react-icons/fa';
import { usePortalSession } from './ClientPortal';
import AnnotationCanvas from './AnnotationCanvas';
import AnnotationToolbar from './AnnotationToolbar';
import AnnotationPanel from './AnnotationPanel';
import ProgressiveImage from './ProgressiveImage';
import usePortalImageUrl from '../hooks/usePortalImageUrl';

const API_BASE = '/api/portal';

// Image card component
function ImageCard({ image, isFavorited, onToggleFavorite, onView, allowFavorites, annotationCount = 0, compact = false }) {
  const { url: imageUrl, loading } = usePortalImageUrl(image, 'sm');

  const handleFavorite = (e) => {
    e.stopPropagation();
    onToggleFavorite(image.id);
  };

  return (
    <div
      className="relative group cursor-pointer overflow-hidden rounded-lg bg-gray-100"
      style={{ aspectRatio: compact ? '1/1' : (image.width && image.height ? `${image.width}/${image.height}` : '4/3') }}
      onClick={() => onView(image)}
    >
      {loading ? (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : imageUrl ? (
        <img
          src={imageUrl}
          alt={image.title || 'Photo'}
          className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
          loading="lazy"
        />
      ) : (
        <div className="absolute inset-0 flex items-center justify-center text-gray-400">
          Failed to load
        </div>
      )}

      {/* Annotation indicator badge */}
      {annotationCount > 0 && (
        <div className="absolute top-2 left-2 flex items-center gap-1 px-2 py-1 bg-amber-500 text-black rounded-full text-xs font-medium shadow">
          <FiEdit3 size={12} />
          {annotationCount}
        </div>
      )}

      {/* Favorite button - always visible on mobile */}
      {allowFavorites && (
        <button
          onClick={handleFavorite}
          className={`absolute top-2 right-2 p-2 rounded-full transition-all
            ${isFavorited ? 'bg-white text-red-500' : 'bg-white/80 text-gray-400 opacity-100 sm:opacity-0 sm:group-hover:opacity-100'}
            hover:bg-white hover:text-red-500 shadow-sm`}
        >
          {isFavorited ? <FaHeart size={18} /> : <FiHeart size={18} />}
        </button>
      )}
    </div>
  );
}

function FocusThumbnail({ image, isActive, onSelect }) {
  const { url: imageUrl } = usePortalImageUrl(image, 'sm');

  return (
    <button
      type="button"
      onClick={onSelect}
      className={`relative overflow-hidden rounded-lg border ${isActive ? 'border-blue-500 ring-2 ring-blue-200' : 'border-transparent'} bg-gray-100 aspect-square`}
      aria-label={`Focus ${image.title || 'photo'}`}
    >
      {imageUrl ? (
        <img src={imageUrl} alt={image.title || 'Photo'} className="w-full h-full object-cover" loading="lazy" />
      ) : (
        <div className="w-full h-full bg-gray-200" />
      )}
    </button>
  );
}

function FocusViewer({
  images,
  focusedIndex,
  onChange,
  onToggleFavorite,
  favorites,
  allowFavorites,
  allowDownloads,
  allowComments,
  onAnnotationChange
}) {
  const { headers } = usePortalSession();
  const image = images[focusedIndex];
  const { url: imageUrl, loading, identity } = usePortalImageUrl(image, 'lg');
  const { url: previewUrl } = usePortalImageUrl(image, 'sm');

  // Annotation state
  const imageContainerRef = useRef(null);
  const [annotations, setAnnotations] = useState([]);
  const [annotationMode, setAnnotationMode] = useState('view');
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false);
  const [pendingShape, setPendingShape] = useState(null);
  const [showInlineComment, setShowInlineComment] = useState(false);

  useEffect(() => {
    setAnnotations([]);
    setSelectedAnnotationId(null);
    setPendingShape(null);
    setShowInlineComment(false);
    setAnnotationMode('view');

    if (!allowComments || !image) return;

    const loadAnnotations = async () => {
      try {
        const res = await fetch(`${API_BASE}/annotations/${image.id}`, { headers });
        if (res.ok) {
          const data = await res.json();
          setAnnotations(data.annotations || []);
        }
      } catch (err) {
        console.error('Failed to load annotations:', err);
      }
    };

    loadAnnotations();
  }, [image?.id, allowComments]);

  const handleShapeComplete = async (shapeType, geometry) => {
    setPendingShape({ shape_type: shapeType, geometry });
    setShowInlineComment(true);
    setAnnotationMode('view');
  };

  const getPopupPosition = () => {
    if (!pendingShape) return { x: 50, y: 50 };
    const { geometry, shape_type } = pendingShape;

    if (shape_type === 'rectangle') {
      return {
        x: (geometry.x + geometry.width / 2) * 100,
        y: (geometry.y + geometry.height) * 100
      };
    }
    if (shape_type === 'circle') {
      return {
        x: geometry.cx * 100,
        y: (geometry.cy + geometry.radius) * 100
      };
    }
    return { x: 50, y: 50 };
  };

  const handleSaveAnnotation = async (comment) => {
    if (!pendingShape || !image) return;

    try {
      const res = await fetch(`${API_BASE}/annotation`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_id: image.id,
          shape_type: pendingShape.shape_type,
          geometry: pendingShape.geometry,
          comment: comment || null
        })
      });

      if (res.ok) {
        const newAnnotation = await res.json();
        setAnnotations(prev => [...prev, newAnnotation]);
        setPendingShape(null);
        setShowInlineComment(false);
        setSelectedAnnotationId(newAnnotation.id);
        if (onAnnotationChange) onAnnotationChange(image.id, 1);
      } else {
        setPendingShape(null);
        setShowInlineComment(false);
      }
    } catch (err) {
      console.error('Failed to save annotation:', err);
      setPendingShape(null);
      setShowInlineComment(false);
    }
  };

  const handleCancelAnnotation = () => {
    setPendingShape(null);
    setShowInlineComment(false);
  };

  const handleAddComment = async (comment) => {
    await handleSaveAnnotation(comment);
  };

  const handleDeleteAnnotation = async (annotationId) => {
    try {
      const res = await fetch(`${API_BASE}/annotation/${annotationId}`, {
        method: 'DELETE',
        headers
      });

      if (res.ok) {
        setAnnotations(prev => prev.filter(a => a.id !== annotationId));
        if (selectedAnnotationId === annotationId) {
          setSelectedAnnotationId(null);
        }
        if (onAnnotationChange) onAnnotationChange(image.id, -1);
      }
    } catch (err) {
      console.error('Failed to delete annotation:', err);
    }
  };

  const goPrev = () => onChange(Math.max(0, focusedIndex - 1));
  const goNext = () => onChange(Math.min(images.length - 1, focusedIndex + 1));

  if (!image) return null;

  const isFavorited = favorites.includes(image.id);
  const displayAnnotations = pendingShape
    ? [...annotations, { id: 'pending', ...pendingShape, status: 'pending' }]
    : annotations;

  return (
    <div className="p-3 sm:p-4 h-[calc(100vh-72px)]">
      <div className="h-full bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
        <div className="relative flex-1 min-h-0 bg-gray-100 flex items-center justify-center">
          {loading && !previewUrl && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          )}
          {(imageUrl || previewUrl) && (
            <div ref={imageContainerRef} className="relative max-w-full max-h-full">
              <ProgressiveImage
                identity={identity}
                previewSrc={previewUrl}
                src={imageUrl}
                alt={image.title || 'Photo'}
                className="max-w-full max-h-full object-contain"
                draggable={false}
              />
              {allowComments && (
                <AnnotationCanvas
                  annotations={displayAnnotations}
                  selectedId={selectedAnnotationId}
                  onSelect={setSelectedAnnotationId}
                  mode={annotationMode}
                  onShapeComplete={handleShapeComplete}
                  containerRef={imageContainerRef}
                />
              )}
              {showInlineComment && pendingShape && (
                <InlineCommentPopup
                  position={getPopupPosition()}
                  onSubmit={handleSaveAnnotation}
                  onCancel={handleCancelAnnotation}
                />
              )}
            </div>
          )}
          {focusedIndex > 0 && annotationMode === 'view' && (
            <button
              type="button"
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 shadow"
              aria-label="Previous photo"
            >
              <FiChevronLeft size={18} />
            </button>
          )}
          {focusedIndex < images.length - 1 && annotationMode === 'view' && (
            <button
              type="button"
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-white/90 shadow"
              aria-label="Next photo"
            >
              <FiChevronRight size={18} />
            </button>
          )}
          {allowComments && (
            <AnnotationToolbar
              mode={annotationMode}
              onModeChange={setAnnotationMode}
              className="absolute bottom-3 right-3"
            />
          )}
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t border-gray-200">
          <div className="min-w-0">
            <div className="text-sm text-gray-500">Photo {focusedIndex + 1} of {images.length}</div>
            <div className="text-gray-900 font-medium truncate">{image.title || image.filename || 'Untitled'}</div>
          </div>
          <div className="flex items-center gap-2">
            {allowComments && (
              <button
                type="button"
                onClick={() => setShowAnnotationPanel(!showAnnotationPanel)}
                className="relative px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50"
                title="Annotations"
              >
                <FiEdit3 size={14} />
                {annotations.length > 0 && (
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-amber-500 rounded-full text-xs flex items-center justify-center text-black font-medium">
                    {annotations.length}
                  </span>
                )}
              </button>
            )}
            {allowFavorites && (
              <button
                type="button"
                onClick={() => onToggleFavorite(image.id)}
                className={`px-3 py-2 rounded-lg border text-sm font-medium ${isFavorited ? 'bg-red-50 text-red-600 border-red-200' : 'bg-white text-gray-600 border-gray-200'}`}
              >
                {isFavorited ? <FaHeart size={14} /> : <FiHeart size={14} />}
              </button>
            )}
            {allowDownloads && (
              <a
                href={`${API_BASE}/download/${image.id}?t=${Date.now()}`}
                className="px-3 py-2 rounded-lg border border-gray-200 text-gray-600 text-sm font-medium hover:bg-gray-50"
              >
                <FiDownload size={14} />
              </a>
            )}
          </div>
        </div>

        <div className="border-t px-3 py-3">
          <div className="flex gap-2 overflow-x-auto pb-1">
            {images.map((img, idx) => (
              <div key={img.id} className="w-16 sm:w-20 flex-shrink-0">
                <FocusThumbnail
                  image={img}
                  isActive={idx === focusedIndex}
                  onSelect={() => onChange(idx)}
                />
              </div>
            ))}
          </div>
        </div>
      </div>

      {allowComments && showAnnotationPanel && (
        <div className="fixed inset-y-0 right-0 z-50">
          <AnnotationPanel
            annotations={annotations}
            selectedId={selectedAnnotationId}
            onSelect={setSelectedAnnotationId}
            onDelete={handleDeleteAnnotation}
            onAddComment={handleAddComment}
            isOpen={showAnnotationPanel}
            onClose={() => setShowAnnotationPanel(false)}
            canDelete={true}
            showCommentInput={!!pendingShape}
          />
        </div>
      )}
    </div>
  );
}

// Inline comment popup that appears at the shape location
function InlineCommentPopup({ position, onSubmit, onCancel }) {
  const [comment, setComment] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setIsSubmitting(true);
    await onSubmit(comment.trim() || null);
    setIsSubmitting(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      onCancel();
    } else if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div
      className="absolute z-10 bg-gray-900 rounded-lg shadow-xl border border-white/20 p-3 w-64"
      style={{
        left: `${position.x}%`,
        top: `${position.y}%`,
        transform: 'translate(-50%, 8px)'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <textarea
        ref={inputRef}
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Add a comment (optional)..."
        className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-white/50 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-amber-500"
        rows={2}
      />
      <div className="flex justify-end gap-2 mt-2">
        <button
          type="button"
          onClick={onCancel}
          className="px-3 py-1 text-sm text-white/70 hover:text-white"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isSubmitting}
          className="px-3 py-1 bg-amber-500 text-black rounded text-sm font-medium hover:bg-amber-400 disabled:opacity-50"
        >
          {isSubmitting ? 'Saving...' : 'Save'}
        </button>
      </div>
      <div className="text-xs text-white/40 mt-1">
        Press Enter to save, Esc to cancel
      </div>
    </div>
  );
}

// Lightbox component with annotation support
function Lightbox({ images, currentIndex, onClose, onPrev, onNext, onToggleFavorite, favorites, allowDownloads, allowFavorites, allowComments, onAnnotationChange }) {
  const { headers } = usePortalSession();
  const image = images[currentIndex];
  const { url: imageUrl, loading, identity } = usePortalImageUrl(image, 'lg');
  const { url: previewUrl } = usePortalImageUrl(image, 'sm');

  // Annotation state
  const imageContainerRef = useRef(null);
  const [annotations, setAnnotations] = useState([]);
  const [annotationMode, setAnnotationMode] = useState('view');
  const [selectedAnnotationId, setSelectedAnnotationId] = useState(null);
  const [showAnnotationPanel, setShowAnnotationPanel] = useState(false);
  const [pendingShape, setPendingShape] = useState(null);
  const [showInlineComment, setShowInlineComment] = useState(false);

  // General notes state (not tied to a shape)
  const [imageNote, setImageNote] = useState('');
  const [savedNote, setSavedNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [showNoteInput, setShowNoteInput] = useState(false);

  useEffect(() => {
    setAnnotations([]);
    setSelectedAnnotationId(null);
    setPendingShape(null);
    setShowInlineComment(false);
    setImageNote('');
    setSavedNote('');
    setShowNoteInput(false);
    if (allowComments) {
      loadAnnotations();
      loadImageNote();
    }
  }, [currentIndex, image.id]);

  const loadAnnotations = async () => {
    try {
      console.log('[Annotation] Loading annotations for image:', image.id);
      const res = await fetch(`${API_BASE}/annotations/${image.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        console.log('[Annotation] Loaded:', data.annotations?.length || 0, 'annotations');
        setAnnotations(data.annotations || []);
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('[Annotation] Load error:', res.status, errorData);
      }
    } catch (err) {
      console.error('[Annotation] Failed to load annotations:', err);
    }
  };

  const loadImageNote = async () => {
    try {
      const res = await fetch(`${API_BASE}/note/${image.id}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setSavedNote(data.note || '');
        setImageNote(data.note || '');
      }
    } catch (err) {
      console.error('Failed to load note:', err);
    }
  };

  const saveImageNote = async () => {
    if (noteSaving) return;
    setNoteSaving(true);
    try {
      const res = await fetch(`${API_BASE}/note/${image.id}`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ note: imageNote.trim() })
      });
      if (res.ok) {
        setSavedNote(imageNote.trim());
        setShowNoteInput(false);
      } else {
        alert('Failed to save note');
      }
    } catch (err) {
      console.error('Failed to save note:', err);
      alert('Failed to save note');
    } finally {
      setNoteSaving(false);
    }
  };

  const handleShapeComplete = async (shapeType, geometry) => {
    setPendingShape({ shape_type: shapeType, geometry });
    setShowInlineComment(true);
    setAnnotationMode('view');
  };

  // Calculate popup position based on shape geometry
  const getPopupPosition = () => {
    if (!pendingShape) return { x: 50, y: 50 };
    const { geometry, shape_type } = pendingShape;

    if (shape_type === 'rectangle') {
      // Position below the center of the rectangle
      return {
        x: (geometry.x + geometry.width / 2) * 100,
        y: (geometry.y + geometry.height) * 100
      };
    } else if (shape_type === 'circle') {
      // Position below the circle
      return {
        x: geometry.cx * 100,
        y: (geometry.cy + geometry.radius) * 100
      };
    }
    return { x: 50, y: 50 };
  };

  const handleSaveAnnotation = async (comment) => {
    if (!pendingShape) return;

    try {
      console.log('[Annotation] Saving annotation:', {
        image_id: image.id,
        shape_type: pendingShape.shape_type,
        geometry: pendingShape.geometry,
        hasComment: !!comment
      });

      const res = await fetch(`${API_BASE}/annotation`, {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          image_id: image.id,
          shape_type: pendingShape.shape_type,
          geometry: pendingShape.geometry,
          comment: comment || null
        })
      });

      if (res.ok) {
        const newAnnotation = await res.json();
        console.log('[Annotation] Saved successfully:', newAnnotation.id);
        setAnnotations(prev => [...prev, newAnnotation]);
        setPendingShape(null);
        setShowInlineComment(false);
        setSelectedAnnotationId(newAnnotation.id);
        // Update count in parent
        if (onAnnotationChange) onAnnotationChange(image.id, 1);
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('[Annotation] Server error:', res.status, errorData);
        alert(`Failed to save annotation: ${errorData.message || 'Unknown error'}`);
        setPendingShape(null);
        setShowInlineComment(false);
      }
    } catch (err) {
      console.error('[Annotation] Network error:', err);
      alert('Failed to save annotation. Please try again.');
      setPendingShape(null);
      setShowInlineComment(false);
    }
  };

  const handleCancelAnnotation = () => {
    setPendingShape(null);
    setShowInlineComment(false);
  };

  const handleAddComment = async (comment) => {
    await handleSaveAnnotation(comment);
  };

  const handleDeleteAnnotation = async (annotationId) => {
    try {
      const res = await fetch(`${API_BASE}/annotation/${annotationId}`, {
        method: 'DELETE',
        headers
      });

      if (res.ok) {
        setAnnotations(prev => prev.filter(a => a.id !== annotationId));
        if (selectedAnnotationId === annotationId) {
          setSelectedAnnotationId(null);
        }
        // Update count in parent
        if (onAnnotationChange) onAnnotationChange(image.id, -1);
      }
    } catch (err) {
      console.error('Failed to delete annotation:', err);
    }
  };

  const handleDownload = async () => {
    if (!allowDownloads) return;
    try {
      const res = await fetch(`${API_BASE}/image/${image.id}/original`, { headers });
      if (res.ok) {
        const data = await res.json();
        window.open(data.url, '_blank');
      }
    } catch (err) {
      console.error('Failed to get download URL:', err);
    }
  };

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (showAnnotationPanel) {
          setShowAnnotationPanel(false);
        } else {
          onClose();
        }
      }
      if (e.key === 'ArrowLeft' && annotationMode === 'view') onPrev();
      if (e.key === 'ArrowRight' && annotationMode === 'view') onNext();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, showAnnotationPanel, annotationMode]);

  const isFavorited = favorites.includes(image.id);

  // Combine saved annotations with pending shape for display
  const displayAnnotations = pendingShape
    ? [...annotations, { id: 'pending', ...pendingShape, status: 'pending' }]
    : annotations;

  return (
    <div className="fixed inset-0 z-50 bg-black flex">
      <div className="flex-1 flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 text-white bg-gradient-to-b from-black/60 to-transparent">
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-lg">
            <FiX size={24} />
          </button>
          <span className="text-sm">{currentIndex + 1} / {images.length}</span>
          <div className="flex items-center gap-2">
            {allowComments && (
              <button
                onClick={() => setShowAnnotationPanel(!showAnnotationPanel)}
                className={`p-2 rounded-lg relative ${showAnnotationPanel ? 'bg-white/20' : 'hover:bg-white/10'}`}
                title="Annotations"
              >
                <FiEdit3 size={24} />
                {annotations.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-amber-500 rounded-full text-xs flex items-center justify-center text-black font-medium">
                    {annotations.length}
                  </span>
                )}
              </button>
            )}
            {allowFavorites && (
              <button
                onClick={() => onToggleFavorite(image.id)}
                className={`p-2 rounded-lg ${isFavorited ? 'text-red-500' : 'hover:bg-white/10'}`}
              >
                {isFavorited ? <FaHeart size={24} /> : <FiHeart size={24} />}
              </button>
            )}
            {allowDownloads && (
              <button onClick={handleDownload} className="p-2 hover:bg-white/10 rounded-lg">
                <FiDownload size={24} />
              </button>
            )}
          </div>
        </div>

        {/* Image with annotation overlay */}
        <div className="flex-1 flex items-center justify-center relative">
          {loading && !previewUrl ? (
            <div className="w-10 h-10 border-3 border-white border-t-transparent rounded-full animate-spin" />
          ) : (imageUrl || previewUrl) ? (
            <div ref={imageContainerRef} className="relative max-w-full max-h-full">
              <ProgressiveImage
                identity={identity}
                previewSrc={previewUrl}
                src={imageUrl}
                alt={image.title || 'Photo'}
                className="max-w-full max-h-[calc(100vh-200px)] object-contain"
                draggable={false}
              />
              {allowComments && (
                <AnnotationCanvas
                  annotations={displayAnnotations}
                  selectedId={selectedAnnotationId}
                  onSelect={setSelectedAnnotationId}
                  mode={annotationMode}
                  onShapeComplete={handleShapeComplete}
                  containerRef={imageContainerRef}
                />
              )}
              {/* Inline comment popup */}
              {showInlineComment && pendingShape && (
                <InlineCommentPopup
                  position={getPopupPosition()}
                  onSubmit={handleSaveAnnotation}
                  onCancel={handleCancelAnnotation}
                />
              )}
            </div>
          ) : null}

          {/* Navigation arrows */}
          {currentIndex > 0 && annotationMode === 'view' && (
            <button
              onClick={onPrev}
              className="absolute left-4 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 hidden sm:block"
            >
              <FiChevronLeft size={28} />
            </button>
          )}
          {currentIndex < images.length - 1 && annotationMode === 'view' && (
            <button
              onClick={onNext}
              className="absolute right-4 p-3 bg-black/50 text-white rounded-full hover:bg-black/70 hidden sm:block"
            >
              <FiChevronRight size={28} />
            </button>
          )}
        </div>

        {/* Annotation toolbar and notes */}
        {allowComments && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2">
            {/* Notes section */}
            {showNoteInput ? (
              <div className="bg-gray-900/95 rounded-lg p-3 w-80 border border-white/20 shadow-xl">
                <textarea
                  value={imageNote}
                  onChange={(e) => setImageNote(e.target.value)}
                  placeholder="Add general notes about this photo..."
                  className="w-full px-3 py-2 bg-white/10 border border-white/20 rounded text-white placeholder-white/50 text-sm resize-none focus:outline-none focus:ring-1 focus:ring-amber-500"
                  rows={3}
                  autoFocus
                />
                <div className="flex justify-end gap-2 mt-2">
                  <button
                    onClick={() => {
                      setImageNote(savedNote);
                      setShowNoteInput(false);
                    }}
                    className="px-3 py-1 text-sm text-white/70 hover:text-white"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={saveImageNote}
                    disabled={noteSaving}
                    className="px-3 py-1 bg-amber-500 text-black rounded text-sm font-medium hover:bg-amber-400 disabled:opacity-50"
                  >
                    {noteSaving ? 'Saving...' : 'Save Note'}
                  </button>
                </div>
              </div>
            ) : savedNote ? (
              <button
                onClick={() => setShowNoteInput(true)}
                className="bg-gray-900/95 rounded-lg px-4 py-2 text-white/80 text-sm max-w-80 truncate border border-white/20 hover:bg-gray-800/95"
              >
                <FiMessageSquare className="inline mr-2" size={14} />
                {savedNote.length > 50 ? savedNote.substring(0, 50) + '...' : savedNote}
              </button>
            ) : (
              <button
                onClick={() => setShowNoteInput(true)}
                className="bg-gray-900/80 rounded-lg px-4 py-2 text-white/60 text-sm border border-white/10 hover:bg-gray-800/95 hover:text-white/80"
              >
                <FiMessageSquare className="inline mr-2" size={14} />
                Add general note...
              </button>
            )}

            <AnnotationToolbar
              mode={annotationMode}
              onModeChange={setAnnotationMode}
            />
          </div>
        )}

        {/* Mobile swipe hint */}
        {annotationMode === 'view' && (
          <div className="p-4 text-center text-white/50 text-sm sm:hidden">
            Swipe to navigate
          </div>
        )}
      </div>

      {/* Annotation panel */}
      {allowComments && (
        <AnnotationPanel
          annotations={annotations}
          selectedId={selectedAnnotationId}
          onSelect={setSelectedAnnotationId}
          onDelete={handleDeleteAnnotation}
          onAddComment={handleAddComment}
          isOpen={showAnnotationPanel}
          onClose={() => setShowAnnotationPanel(false)}
          canDelete={true}
          showCommentInput={!!pendingShape}
        />
      )}
    </div>
  );
}

// Main gallery viewer
export default function ClientGalleryViewer() {
  const { token } = useParams();
  const navigate = useNavigate();
  const [sessionHeaders, setSessionHeaders] = useState({});

  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(null);
  const [needsPassword, setNeedsPassword] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [gallery, setGallery] = useState(null);
  const [images, setImages] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [lightboxIndex, setLightboxIndex] = useState(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const [viewMode, setViewMode] = useState('grid');
  const [annotationCounts, setAnnotationCounts] = useState({}); // { imageId: count }

  useEffect(() => {
    authenticateAndLoad();
  }, [token]);

  const authenticateAndLoad = async (enteredPassword = null) => {
    try {
      setLoading(true);
      setPasswordError(null);

      // Authenticate with the token (and password if provided)
      const authRes = await fetch(`${API_BASE}/auth`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password: enteredPassword })
      });

      if (!authRes.ok) {
        const error = await authRes.json();

        // Check if password is required
        if (error.error === 'password_required') {
          setNeedsPassword(true);
          setLoading(false);
          return;
        }

        // Check if password was wrong
        if (error.error === 'invalid_password') {
          setPasswordError('Incorrect password');
          setLoading(false);
          return;
        }

        setAuthError(error.error || 'Invalid or expired link');
        setLoading(false);
        return;
      }

      const authData = await authRes.json();
      const sessionToken = authData.session_token;

      // Store session
      sessionStorage.setItem('portal_session', sessionToken);

      const headers = { 'Authorization': `Bearer ${sessionToken}` };
      setSessionHeaders(headers);
      setNeedsPassword(false);

      // Now load the gallery
      const galleryRes = await fetch(`${API_BASE}/gallery`, { headers });
      if (galleryRes.ok) {
        const data = await galleryRes.json();
        setGallery(data.gallery);
        setImages(data.images);
        setFavorites(data.favorites || []);

        // Load annotation counts for all images
        const countsRes = await fetch(`${API_BASE}/annotation-counts`, { headers });
        if (countsRes.ok) {
          const countsData = await countsRes.json();
          setAnnotationCounts(countsData.counts || {});
        }
      }
    } catch (err) {
      console.error('Failed to load gallery:', err);
      setAuthError('Failed to load gallery');
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (password.trim()) {
      authenticateAndLoad(password);
    }
  };

  const toggleFavorite = async (imageId) => {
    const wasFavorited = favorites.includes(imageId);

    // Optimistic update
    setFavorites(prev =>
      wasFavorited
        ? prev.filter(id => id !== imageId)
        : [...prev, imageId]
    );

    try {
      const res = await fetch(`${API_BASE}/favorite/${imageId}`, {
        method: 'POST',
        headers: sessionHeaders
      });

      if (!res.ok) {
        // Revert on failure
        setFavorites(prev =>
          wasFavorited
            ? [...prev, imageId]
            : prev.filter(id => id !== imageId)
        );
      }
    } catch (err) {
      // Revert on error
      setFavorites(prev =>
        wasFavorited
          ? [...prev, imageId]
          : prev.filter(id => id !== imageId)
      );
    }
  };

  const openLightbox = (image) => {
    const index = images.findIndex(img => img.id === image.id);
    setFocusedIndex(index >= 0 ? index : 0);
    if (viewMode === 'focus') return;
    setLightboxIndex(index);
  };

  const closeLightbox = () => setLightboxIndex(null);
  const prevImage = () => setLightboxIndex(i => Math.max(0, i - 1));
  const nextImage = () => setLightboxIndex(i => Math.min(images.length - 1, i + 1));
  const selectFocus = (idx) => setFocusedIndex(Math.min(Math.max(0, idx), images.length - 1));
  const switchToGrid = () => setViewMode('grid');
  const switchToFocus = () => {
    if (focusedIndex < 0 || focusedIndex >= images.length) {
      setFocusedIndex(0);
    }
    setLightboxIndex(null);
    setViewMode('focus');
  };

  // Update annotation count when annotations change in lightbox
  const updateAnnotationCount = (imageId, delta) => {
    setAnnotationCounts(prev => ({
      ...prev,
      [imageId]: Math.max(0, (prev[imageId] || 0) + delta)
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-10 h-10 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (authError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500 mb-2">{authError}</p>
          <p className="text-sm text-gray-400">This link may have expired or been revoked.</p>
        </div>
      </div>
    );
  }

  if (needsPassword) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-sm w-full mx-4">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FiLock className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900">Password Protected</h2>
            <p className="text-sm text-gray-500 mt-1">Enter the password to view this gallery</p>
          </div>

          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter password"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              autoFocus
            />
            {passwordError && (
              <p className="text-red-500 text-sm mt-2">{passwordError}</p>
            )}
            <button
              type="submit"
              disabled={!password.trim() || loading}
              className="w-full mt-4 px-4 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Verifying...' : 'View Gallery'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!gallery) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Gallery not found</p>
      </div>
    );
  }

  // Get session permissions from token
  const sessionData = JSON.parse(atob(sessionStorage.getItem('portal_session')?.split('.')[1] || 'e30='));
  const allowFavorites = sessionData.allow_favorites;
  const allowDownloads = sessionData.allow_downloads;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-gray-200">
        <div className="px-4 py-3 flex items-center gap-4">
          <button
            onClick={() => navigate(`/c/${token}`)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <FiArrowLeft size={20} />
          </button>
          <div className="min-w-0 flex-1">
            <h1 className="font-semibold text-gray-900 truncate">{gallery.title}</h1>
            {gallery.job_title && (
              <p className="text-sm text-gray-500 truncate">{gallery.job_title}</p>
            )}
          </div>
          <div className="ml-auto flex items-center gap-3">
            {allowFavorites && favorites.length > 0 && (
              <span className="px-2 py-1 bg-red-50 text-red-600 rounded-full text-sm flex items-center gap-1">
                <FaHeart size={12} />
                {favorites.length}
              </span>
            )}
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={switchToGrid}
                className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 ${viewMode === 'grid' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}
              >
                <FiGrid size={16} />
                Grid
              </button>
              <button
                type="button"
                onClick={switchToFocus}
                className={`px-3 py-2 rounded-lg border text-sm font-medium flex items-center gap-2 ${viewMode === 'focus' ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200'}`}
              >
                <FiMaximize2 size={16} />
                Focus
              </button>
            </div>
          </div>
        </div>
      </header>

      {viewMode === 'focus' ? (
        <FocusViewer
          images={images}
          focusedIndex={focusedIndex}
          onChange={selectFocus}
          onToggleFavorite={toggleFavorite}
          favorites={favorites}
          allowFavorites={allowFavorites}
          allowDownloads={allowDownloads}
          allowComments={sessionData.allow_comments}
          onAnnotationChange={updateAnnotationCount}
        />
      ) : (
        /* Gallery grid */
        <div className="p-2 sm:p-4">
          {images.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">No photos in this gallery</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-1.5 sm:gap-2">
              {images.map(image => (
                <ImageCard
                  key={image.id}
                  image={image}
                  isFavorited={favorites.includes(image.id)}
                  onToggleFavorite={toggleFavorite}
                  onView={openLightbox}
                  allowFavorites={allowFavorites}
                  annotationCount={annotationCounts[image.id] || 0}
                  compact
                />
              ))}
            </div>
          )}
        </div>
      )}

      {/* Lightbox */}
      {lightboxIndex !== null && (
        <Lightbox
          images={images}
          currentIndex={lightboxIndex}
          onClose={closeLightbox}
          onPrev={prevImage}
          onNext={nextImage}
          onToggleFavorite={toggleFavorite}
          favorites={favorites}
          allowDownloads={allowDownloads}
          allowFavorites={allowFavorites}
          allowComments={sessionData.allow_comments}
          onAnnotationChange={updateAnnotationCount}
        />
      )}
    </div>
  );
}
